import React, { forwardRef, useRef, useImperativeHandle, useState } from 'react';
import { Map, APILoader, DrawingManager } from '@uiw/react-baidu-map';

import { IMapOptionProps, IMapPoint } from './types';
import { uuid } from '@helpers/utils';
import { sortDistanceData } from './mapUtils';
import { BMAPKEY } from '@helpers/Constants';


const BMapContent = (props: IMapOptionProps, ref: any) => {
  const {
    center,
    zoom,
    minZoom,
    maxZoom,
    enablePinchToZoom = true,
    enableDoubleClickZoom = true,
    onZoomEnd = null,
    onTouchStart,
  } = props;
  const mapRef = useRef<any>(null);
  const mouseToolRef = useRef<any>(null);
  const drawType = useRef('');
  const [isFirstLoaded, setFirstLoaded] = useState(false);
  const [mapCenter] = useState(center);
  useImperativeHandle(ref, () => ({
    zoomIn,
    zoomOut,
    getMapRef: () => mapRef.current?.map,
    getZoom,
    setViewport,
    getLocation,
    getOverlays,
    clearOverlays,
    clearOverlayByType,
    removeOverlay,
    addOverlay,
    createPoint,
    createPolyline,
    setCenter,
    getCenter,
    enableDragging,
    disableDragging,
    addListener,
    removeListener,
    locationSearch,
    getLocationPois,
    setZoomAndCenter,
    setZoom,
    initMouseTool,
    mouseDraw,
    mouseToolClose,
    setOverlayStyle,
  }));

  const initMouseTool = (callback: (data: any) => void, styleOptions = {}) => {
    console.log(initMouseTool, 'initMouseTool');
    const instanceMap = mapRef.current.map;
    if (!mouseToolRef.current) {
      mouseToolRef.current = new BMapLib.DrawingManager(instanceMap, {
        isOpen: false,
        enableDrawingTool: false,
        enableCalculate: false,
        circleOptions: styleOptions,
        polygonOptions: styleOptions,
      });
    }
    addMouseToolListeners(callback);
  };

  const addMouseToolListeners = (callback: (data: any) => void) => {
    mouseToolRef.current.addEventListener('polygoncomplete', function (e: any, overlay: any) {
      const tempPoints = e.getPath();
      const data = {
        type: drawType.current,
        path: tempPoints.map((item: any) => [item.lng, item.lat]),
      };
      console.log(tempPoints, data, 'tempPoints polygon');
      callback && callback(data);
    });

    mouseToolRef.current.addEventListener('circlecomplete', function (e: any, overlay: any) {
      const tempPoints = [e.getCenter()];
      const data = {
        type: drawType.current,
        path: [tempPoints[0].lng, tempPoints[0].lat],
        radius: e.getRadius(),
      };
      callback && callback(data);
    });
  };

  const mouseToolClose = () => {
    mouseToolRef.current.close();
  };

  const mouseDraw = (type: string, options: { [key: string]: any }) => {
    drawType.current = type;
    mouseToolRef.current.circleOptions = options;
    mouseToolRef.current.polygonOptions = options;
    console.log(mouseToolRef.current, 'mouseToolRef.current')
    mouseToolRef.current.open();
    if (type === 'circle') {
      mouseToolRef.current.setDrawingMode('circle');
    }
    if (type === 'polygon') {
      mouseToolRef.current.setDrawingMode('polygon');
    }
  };

  const setOverlayStyle = (styles: { fillColor?: string; strokeColor?: string }) => {
    const allOverlays = getOverlays();
    const currentOverlay = allOverlays.find(
      (item: BMap.Polygon) =>
        item.toString() === '[object Polygon]' || item.toString() === '[object Circle]',
    );
    console.log(currentOverlay, 'currentOverlay');
    currentOverlay.setStrokeColor(styles.strokeColor);
    currentOverlay.setFillColor(styles.fillColor);
  };

  const locationSearch = (value: string, center: IMapPoint) => {
    const instanceMap = mapRef.current.map;
    return new Promise((resolve, reject) => {
      try {
        const options = {
          onSearchComplete: (results: any) => {
            let list = [];
            let listNum = results.getCurrentNumPois();

            if (listNum > 0) {
              for (var i = 0; i < listNum; i++) {
                list.push(results.getPoi(i));
              }
              list = list.map((item: any, index: any) => {
                return {
                  ...item,
                  uniqueId: uuid(), //给每个地址结果加个uniqueId
                  distance: instanceMap.getDistance(
                    new BMap.Point(center.lng, center.lat),
                    new BMap.Point(item.point.lng, item.point.lat),
                  ),
                };
              });
              list = sortDistanceData(list);
            }
            resolve(list);
          },
        };
        const local = new BMap.LocalSearch(instanceMap, options);
        local.search(value);
        local.setPageCapacity(100);
      } catch {}
    });
  };

  const addListener = (eventName: string, callback: () => void) => {
    const instanceMap = mapRef.current.map;
    instanceMap.addEventListener(eventName, callback);
  };

  const removeListener = (eventName: string, callback: () => void) => {
    const instanceMap = mapRef.current.map;
    instanceMap.removeEventListener(eventName, callback);
  };

  const enableDragging = () => {
    const instanceMap = mapRef.current.map;
    instanceMap.enableDragging();
  };
  const disableDragging = () => {
    const instanceMap = mapRef.current.map;
    instanceMap.disableDragging();
  };

  const getCenter = () => {
    const instanceMap = mapRef.current.map;
    const center = instanceMap.getCenter();
    return center;
  };



  const setViewport = (viewport?: { points: any[]; margins: number }) => {
    const instanceMap = mapRef.current.map;
    instanceMap.setViewport(viewport?.points || [], viewport?.margins || []);
  };

  const setCenter = (center: { lng: number; lat: number }) => {
    const instanceMap = mapRef.current.map;
    console.log(instanceMap, center, 'instanceMap');
    instanceMap.setCenter(new BMap.Point(center.lng, center.lat));
  };

  const setZoomAndCenter = (center: IMapPoint, zoom: number) => {
    const instanceMap = mapRef.current.map;
    instanceMap.centerAndZoom(new BMap.Point(center.lng, center.lat), zoom);
  };

  const getZoom = () => {
    const instanceMap = mapRef.current.map;
    console.log(instanceMap, 'instanceMap');
    return instanceMap.getZoom();
  };

  const setZoom = (zoom: number) => {
    const instanceMap = mapRef.current.map;
    return instanceMap.setZoom(zoom);
  }
  const createPoint = (item: { lng: number; lat: number }) => {
    const Point = new BMap.Point(item.lng, item.lat);
    return Point;
  };

  const createPolyline = (path: [{ lng: number; lat: number }], options: BMap.PolylineOptions) => {
    const Polyline = new BMap.Polyline(path, {
      ...options,
    });
    return Polyline;
  };

  const clearOverlays = () => {
    const instanceMap = mapRef.current.map;
    instanceMap.clearOverlays();
  };

  const clearOverlayByType = (type?: string) => {
    const instanceMap = mapRef.current.map;
    const overlays = getOverlays();
    if (overlays?.length) {
      return;
    }
    if (type === 'Polyline') {
      const tempOverlays = overlays.find((i: any) => i.toString() === '[object Polyline]');
      console.log(tempOverlays, 'tempOverlays');
      if (tempOverlays?.length) {
        instanceMap.remove(tempOverlays);
      }
    }
  };

  const removeOverlay = (overlay: BMap.Overlay) => {
    const instanceMap = mapRef.current.map;
    instanceMap.removeOverlay(overlay);
  };

  const addOverlay = (overlay: BMap.Overlay) => {
    const instanceMap = mapRef.current.map;
    instanceMap.addOverlay(overlay);
  };
  const onLoad = () => {
    if (!isFirstLoaded) {
      setFirstLoaded(true);
      props.onMapLoad && props.onMapLoad();
    }
  };

  const getOverlays = () => {
    const instanceMap = mapRef.current.map;
    const allOverlays = instanceMap.getOverlays();
    return allOverlays;
  };

  const getLocation = async (position: { lng: number; lat: number }) => {
    const map = new BMap.Geocoder();
    return new Promise((resolve) => {
      map.getLocation(new BMap.Point(position.lng, position.lat), function (res: any) {
        resolve({ address: res.address, title: res.title || '' });
      });
    });
  };

  const getLocationPois = async (position: IMapPoint, r: number) => {
    const instanceMap = mapRef.current.map;
    const map = new BMap.Geocoder();
    return new Promise((resolve) => {
      map.getLocation(
        new BMap.Point(position.lng, position.lat),
        function (result) {
          const currentPoint = {
            ...position,
            address: result.address || '位置',
            title: result.address,
            distance: 0,
            uniqueId: uuid(),
          };
          let list = (result as any)?.surroundingPois || [];
          list = list.slice(0, 10).map((item: any, index: any) => {
            return {
              ...item,
              uniqueId: uuid(),
              distance: instanceMap.getDistance(
                new BMap.Point(position.lng, position.lat),
                new BMap.Point(item.point.lng, item.point.lat),
              ),
            };
          });
          list.unshift(currentPoint);
          list = sortDistanceData(list);
          resolve(list);
        },
        { poiRadius: r },
      );
    });
  };

  const zoomIn = () => {
    const instanceMap = mapRef.current.map;
    if (instanceMap) {
      instanceMap.setZoom(getZoom() + 1);
    }
  };
  const zoomOut = () => {
    const instanceMap = mapRef.current.map;
    if (instanceMap) {
      instanceMap.setZoom(getZoom() - 1);
    }
  };
  return (
    <APILoader akay={BMAPKEY} version="2.0">
      <Map
        zoom={zoom || 17}
        maxZoom={maxZoom || 19}
        minZoom={minZoom || 1}
        center={mapCenter}
        // mapStyleV2={{styleJson: json}}
        ref={mapRef}
        onTilesLoaded={onLoad}
        enableScrollWheelZoom={true}
        enablePinchToZoom={enablePinchToZoom}
        enableDoubleClickZoom={enableDoubleClickZoom}
        enableHighResolution={true}
        onZoomEnd={onZoomEnd}
        onTouchStart={onTouchStart}
      >
        {props.children}
        <DrawingManager isOpen={false} enableDrawingTool={false} enableCalculate={true} />
      </Map>
    </APILoader>
  );
};

export default forwardRef(BMapContent);
